import { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Button, Chip } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { authHeader } from "../auth";
import { usePresencePing } from "../hooks/usePresencePing";
import ParallelSubNav from "./ParallelSubNav";

export default function Bibs() {
    const { category } = useParams();
    usePresencePing(category, "Couleurs");
    const [bracket, setBracket] = useState(null);
    const navigate = useNavigate();

    // -------------------------
    // API FETCH AVEC TOKEN (authHeader() plutôt qu'un token lu à la main)
    // -------------------------
    const apiFetch = useCallback(
        async (url, options = {}) => {
            const res = await fetch(url, {
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    ...authHeader(),
                    ...(options.headers || {}),
                },
            });

            if (res.status === 401) {
                navigate("/login");
                return null;
            }

            if (res.status === 403) {
                console.error("Accès interdit");
                return null;
            }

            return res;
        },
        [navigate]
    );

    // -------------------------
    // Charger le bracket
    // -------------------------
    useEffect(() => {
        const load = async () => {
            let res = await apiFetch(`/api/finals/${category}`);
            if (!res) return;

            let data = await res.json();

            // Si pas de bracket → le générer automatiquement
            if (!data || !data.rounds) {
                await apiFetch(`/api/finals/generate/${category}`, { method: "POST" });

                res = await apiFetch(`/api/finals/${category}`);
                if (!res) return;

                data = await res.json();
            }

            setBracket(data);
        };

        load();
    }, [category, apiFetch]);

    // -------------------------
    // Définir la couleur
    // -------------------------
    const setColor = async (roundId, athlete, color) => {
        await apiFetch(`/api/finals/set-color/${category}/${roundId}`, {
            method: "POST",
            body: JSON.stringify({ athlete, color }),
        });

        const updatedRes = await apiFetch(`/api/finals/${category}`);
        if (!updatedRes) return;

        setBracket(await updatedRes.json());
    };

    // -------------------------
    // Export Start List
    // -------------------------
    const exportStartList = async (roundIndex) => {
        const res = await apiFetch(`/api/finals/export-round/${category}/${roundIndex}`);

        if (!res) {
            console.error("Erreur PDF: Unauthorized/Forbidden");
            return;
        }

        if (!res.ok) {
            console.error("Erreur PDF:", await res.text());
            return;
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `startlist_${category}_round${roundIndex}.pdf`;
        a.click();
    };

    // -------------------------
    // Rendu
    // -------------------------
    if (!bracket) {
        return (
            <Box p={3}>
                <ParallelSubNav category={category} current="bibs" />
                <Typography>Chargement du bracket…</Typography>
            </Box>
        );
    }

    if (!bracket.rounds || bracket.rounds.length === 0) {
        return (
            <Box p={3}>
                <ParallelSubNav category={category} current="bibs" />
                <Typography variant="h5" color="error">
                    Le bracket n'est pas disponible ou n'a pas pu être généré.
                </Typography>
                <Typography variant="body1" mt={2}>
                    Vérifie que les qualifications ont été importées correctement.
                </Typography>
            </Box>
        );
    }

    // Rendu d'une carte de duel (couleurs) — réutilisé pour les tours
    // principaux ET la petite finale (même logique : le mieux classé choisit).
    const renderDuelCard = (duel) => {
        const A = duel.athleteA;
        const B = duel.athleteB;

        if (!A || !B) return null;

        const better = A.qualifRank < B.qualifRank ? "A" : "B";

        return (
            <Box key={duel.id} sx={{ width: 280 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h6">{duel.id}</Typography>

                        {/* ATHLETE A */}
                        <Box mt={2}>
                            <Typography>
                                <strong>A:</strong> {A.name} (bib {A.bib})
                            </Typography>

                            {duel.colorA && (
                                <Chip
                                    label={duel.colorA.toUpperCase()}
                                    color={duel.colorA === "blue" ? "primary" : "error"}
                                    sx={{ mt: 1 }}
                                />
                            )}

                            {better === "A" && (
                                <Box mt={1}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => setColor(duel.id, "A", "blue")}
                                        sx={{ mr: 1 }}
                                    >
                                        Bleu
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() => setColor(duel.id, "A", "red")}
                                    >
                                        Rouge
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        {/* ATHLETE B */}
                        <Box mt={3}>
                            <Typography>
                                <strong>B:</strong> {B.name} (bib {B.bib})
                            </Typography>

                            {duel.colorB && (
                                <Chip
                                    label={duel.colorB.toUpperCase()}
                                    color={duel.colorB === "blue" ? "primary" : "error"}
                                    sx={{ mt: 1 }}
                                />
                            )}

                            {better === "B" && (
                                <Box mt={1}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => setColor(duel.id, "B", "blue")}
                                        sx={{ mr: 1 }}
                                    >
                                        Bleu
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() => setColor(duel.id, "B", "red")}
                                    >
                                        Rouge
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    };

    const scrollToRound = (anchorId) => {
        document.getElementById(anchorId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <Box p={3}>
            <ParallelSubNav category={category} current="bibs" />

            <Typography variant="h4" mb={3}>
                Attribution des couleurs – {category}
            </Typography>

            {/* Navigation rapide vers un tour précis */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {bracket.rounds.map((round, i) => (
                    <Chip
                        key={i}
                        label={round[0]?.label || `Tour ${i + 1}`}
                        clickable
                        onClick={() => scrollToRound(`round-${i}`)}
                    />
                ))}
                {bracket.smallFinal && (bracket.smallFinal.athleteA || bracket.smallFinal.athleteB) && (
                    <Chip
                        label="Petite finale (3e/4e)"
                        clickable
                        color="warning"
                        onClick={() => scrollToRound("round-small-final")}
                    />
                )}
            </Box>

            {bracket.rounds.map((round, roundIndex) => (
                <Box key={roundIndex} id={`round-${roundIndex}`} mb={4}>
                    <Typography variant="h5" mb={2}>
                        {round[0]?.label}
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {round.map((duel) => renderDuelCard(duel))}
                    </Box>

                    <Box mt={3}>
                        <Button variant="contained" onClick={() => exportStartList(roundIndex)}>
                            Télécharger Start List
                        </Button>
                    </Box>
                </Box>
            ))}

            {bracket.smallFinal && (bracket.smallFinal.athleteA || bracket.smallFinal.athleteB) && (
                <Box id="round-small-final" mb={4}>
                    <Typography variant="h5" mb={2}>
                        {bracket.smallFinal.label || "Petite finale (3e / 4e)"}
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {renderDuelCard(bracket.smallFinal)}
                    </Box>
                </Box>
            )}
        </Box>
    );
}